import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Funnel,
  Target,
  TrendUp,
  UserPlus,
  Users,
} from "@phosphor-icons/react";

import { acquisition as acquisitionApi, prospects as prospectsApi } from "@/api";
import ActaStandardPage from "@/components/layout/ActaStandardPage";
import CommerceMetricCard from "@/components/commerce/CommerceMetricCard";

const EMPTY = {
  received: 0,
  new: 0,
  toAssign: 0,
  assigned: 0,
  withOpportunity: 0,
  withWon: 0,
  assignmentRate: 0,
  opportunityRate: 0,
  wonRate: 0,
  bySource: [],
  byCampaign: [],
  byCommercial: [],
};

function Breakdown({ title, rows }) {
  return (
    <section className="lirah-card p-5">
      <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">{title}</h2>
      <div className="mt-4 space-y-3">
        {rows?.length ? rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-4 border-b border-[var(--do-border)] pb-2 text-sm last:border-0">
            <span className="truncate text-[var(--do-muted)]">{row.label}</span>
            <strong className="text-[var(--do-ink)]">{row.count}</strong>
          </div>
        )) : (
          <p className="text-sm text-[var(--do-muted)]">Aucune donnée disponible.</p>
        )}
      </div>
    </section>
  );
}

export default function AcquisitionDashboard() {
  const navigate = useNavigate();
  const openLeads = (view) => {
    const params = new URLSearchParams({ ...filters, view });
    navigate(`/app/acquisition/leads?${params.toString()}`);
  };
  const [data, setData] = useState(EMPTY);
  const [sources, setSources] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [filters, setFilters] = useState({
    source_id: "",
    campaign_id: "",
    assigned_user_id: "",
    from_date: "",
    to_date: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setData(EMPTY);
    Promise.all([
      acquisitionApi.dashboard(filters),
      acquisitionApi.listSources(),
      acquisitionApi.listCampaigns(),
      prospectsApi.listCommercialUsers(),
    ])
      .then(([response, sourceRows, campaignRows, commercialRows]) => {
        if (!active) return;
        setData({ ...EMPTY, ...(response || {}) });
        setSources(Array.isArray(sourceRows) ? sourceRows : []);
        setCampaigns(Array.isArray(campaignRows) ? campaignRows : []);
        setCommercials(Array.isArray(commercialRows) ? commercialRows : []);
      })
      .catch((err) => {
        if (active) {
          setData(null);
          setError(err?.response?.data?.detail || "Impossible de charger le tableau de bord.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [filters, retryKey]);

  return (
    <ActaStandardPage testId="acquisition-dashboard">
      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <select className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm" value={filters.source_id} onChange={(e) => setFilters((current) => ({ ...current, source_id: e.target.value }))}>
          <option value="">Toutes les sources</option>
          {sources.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm" value={filters.campaign_id} onChange={(e) => setFilters((current) => ({ ...current, campaign_id: e.target.value }))}>
          <option value="">Toutes les campagnes</option>
          {campaigns.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm" value={filters.assigned_user_id} onChange={(e) => setFilters((current) => ({ ...current, assigned_user_id: e.target.value }))}>
          <option value="">Tous les commerciaux</option>
          {commercials.map((item) => <option key={item.id} value={item.id}>{item.name || item.email}</option>)}
        </select>
        <label className="flex flex-col gap-1 text-sm text-[var(--do-muted)]">Du
        <input aria-label="Date de début" type="date" className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm" value={filters.from_date ? filters.from_date.slice(0, 10) : ""} max={filters.to_date ? filters.to_date.slice(0, 10) : undefined} onChange={(e) => setFilters((current) => ({ ...current, from_date: e.target.value ? `${e.target.value}T00:00:00+00:00` : "" }))} />
        </label>
        <label className="flex flex-col gap-1 text-sm text-[var(--do-muted)]">Au
        <input aria-label="Date de fin" type="date" className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm" value={filters.to_date ? filters.to_date.slice(0, 10) : ""} min={filters.from_date ? filters.from_date.slice(0, 10) : undefined} onChange={(e) => setFilters((current) => ({ ...current, to_date: e.target.value ? `${e.target.value}T23:59:59+00:00` : "" }))} />
        </label>
      </div>

      {error ? (
        <div className="mb-5 rounded-2xl border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-4 text-sm text-[var(--do-danger)]">
          {error}
          <button type="button" className="btn-secondary ml-3 px-3 py-2" onClick={() => setRetryKey((key) => key + 1)}>Réessayer</button>
        </div>
      ) : null}

      {!error && <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <CommerceMetricCard onClick={() => openLeads("all")} label="Leads reçus" value={loading ? "…" : data.received} detail="Tous les leads Acquisition" icon={Users} />
        <CommerceMetricCard onClick={() => openLeads("new")} label="Nouveaux" value={loading ? "…" : data.new} detail="À contrôler ou doublons potentiels" icon={UserPlus} />
        <CommerceMetricCard onClick={() => openLeads("to_assign")} label="À affecter" value={loading ? "…" : data.toAssign} detail="Prêts pour attribution" icon={Funnel} />
        <CommerceMetricCard onClick={() => openLeads("assigned")} label="Affectés" value={loading ? "…" : data.assigned} detail={`${data.assignmentRate}% des leads reçus`} icon={CheckCircle} />
        <CommerceMetricCard onClick={() => openLeads("opportunity")} label="Avec affaire" value={loading ? "…" : data.withOpportunity} detail={`${data.opportunityRate}% des leads affectés`} icon={Target} />
        <CommerceMetricCard onClick={() => openLeads("won")} label="Affaires gagnées" value={loading ? "…" : data.withWon} detail={`${data.wonRate}% des leads affectés`} icon={TrendUp} />
      </div>}

      {!error && <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Breakdown title="Par source" rows={data.bySource} />
        <Breakdown title="Par campagne" rows={data.byCampaign} />
        <Breakdown title="Par commercial initial" rows={data.byCommercial} />
      </div>}
    </ActaStandardPage>
  );
}
